import React, { useState, useEffect, useRef } from "react";
import config from "../../../../config/config";
import { apiCall } from "../../../../_services/apiCall";
import AnalyticsTable from "./AnalyticsTable";
import MultiSelect from "../../Component/CommonComponent/MultiSelect";
import { KpiIcon } from "../../../DataManagment/Quantative/component/KpiIcons";
import { useAnalyticsData } from "./useAnalyticsData";
import {
  BIOMEDICAL_DERIVED_EXTRA_QIDS,
  applyBiomedicalDerivedTriggers,
  WATER_DERIVED_EXTRA_QIDS,
  applyWaterDerivedTriggers,
} from "./derivedTriggers";
import { BIOMEDICAL_QUESTION_IDS, WATER_QUESTION_IDS } from "./constants";

export { getDaysInMonth } from "./helpers";

/**
 * The data-analytics page: financial-year, location and month filters above a
 * Bio-Medical Waste and a Water Management tab, each rendered by AnalyticsTable.
 *
 * Owns only page-level selection state — the two tabs' data is fetched by
 * useAnalyticsData, and the idle tab is kept idle by passing it a null FY. The
 * Bio-Medical tab exists only for companies reporting under framework 48.
 */
const DateShow = () => {
  const [sourceLabels, setUserLabels] = useState({});
  const [sourceIds, setSourceIds] = useState([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState([]);
  const [financialYear, setFinancialYear] = useState([]);
  const [financialYearId, setFinancialYearId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [months, setMonths] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));

  /**
   * Whether this company reports under framework 48, which is what makes the
   * Bio-Medical Waste tab applicable. Without it the page is water-only.
   */
  const hasFramework48 = currentUser?.frameworkData?.some(
    (item) => item.id === 48
  );

  const [activeTab, setActiveTab] = useState(
    hasFramework48 ? "bioMedical" : "water"
  );

  const isMounted = useRef(true);

  /**
   * The twelve months of a financial year in reporting order — April of the
   * start year through March of the end year — each as
   * `{ month, year, name, value: "YYYY-MM", fullName }`.
   * @param {Object} financialYearData  an FY row carrying `financial_year_value`
   * @returns {Array} the months, or `[]` when the FY has no parseable value
   */
  const generateMonthsFromFinancialYear = (financialYearData) => {
    if (!financialYearData || !financialYearData.financial_year_value) {
      return [];
    }

    const yearMatch =
      financialYearData.financial_year_value.match(/(\d{4})-(\d{4})/);
    if (!yearMatch) {
      return [];
    }

    const startYear = parseInt(yearMatch[1]);
    const endYear = parseInt(yearMatch[2]);

    const monthsArray = [];

    for (let month = 4; month <= 12; month++) {
      const monthName = new Date(startYear, month - 1, 1).toLocaleString(
        "default",
        { month: "short" }
      );
      monthsArray.push({
        month: month,
        year: startYear,
        name: monthName,
        value: `${startYear}-${month.toString().padStart(2, "0")}`,
        fullName: `${monthName} ${startYear}`,
      });
    }

    for (let month = 1; month <= 3; month++) {
      const monthName = new Date(endYear, month - 1, 1).toLocaleString(
        "default",
        { month: "short" }
      );
      monthsArray.push({
        month: month,
        year: endYear,
        name: monthName,
        value: `${endYear}-${month.toString().padStart(2, "0")}`,
        fullName: `${monthName} ${endYear}`,
      });
    }

    return monthsArray;
  };

  /**
   * One entry per selected reporting SCOPE — a source, or a specific
   * sub-location of one. Analytics is broken down by (location, sub-location),
   * so KMC-T1 and KMC-T2 each get their own section and their own values rather
   * than sharing one collapsed site-wide aggregate.
   *
   * An empty selection, or one covering every option, means "all scopes".
   * @returns {Array<{id: number, subLocationId: (number|null), name: string}>}
   */
  const getLocationData = () => {
    const locationKeys =
      selectedSourceIds.length === 0 ||
        selectedSourceIds.length === sourceIds.length
        ? sourceIds
        : selectedSourceIds;

    return locationKeys.map((locationKey) => {
      const [rawId, rawSubId] = String(locationKey).split("-");
      const id = Number(rawId);
      return {
        id,
        subLocationId: rawSubId === undefined ? null : Number(rawSubId),
        name:
          sourceLabels[locationKey] ||
          sourceLabels[String(id)] ||
          `Location ${id}`,
      };
    });
  };

  /**
   * MultiSelect options, one per reportable scope key in `sourceIds` — a source
   * id, or `<sourceId>-<subLocationId>` for a sub-location. `count` is a fixed 1
   * because the picker has no per-scope tally to show.
   * @returns {Array<{value: string, label: string, count: number}>}
   */
  const getLocationOptions = () => {
    return sourceIds.map((id) => ({
      value: id.toString(),
      label: sourceLabels[id] || `Location ${id}`,
      count: 1,
    }));
  };

  /** The scopes both tabs render, recomputed from the current selection. */
  const locations = getLocationData();

  /** Bio-Medical tab data — idle unless that tab is the active one. */
  const biomedical = useAnalyticsData({
    financialYearId: activeTab === "bioMedical" ? financialYearId : null,
    selectedMonth,
    locations,
    financialYear,
    questionIds: BIOMEDICAL_QUESTION_IDS,
    extraAnalyticsQids: BIOMEDICAL_DERIVED_EXTRA_QIDS,
    applyDerivedTriggers: applyBiomedicalDerivedTriggers,
  });

  /** Water tab data — idle unless that tab is the active one. */
  const water = useAnalyticsData({
    financialYearId: activeTab === "water" ? financialYearId : null,
    selectedMonth,
    locations,
    financialYear,
    questionIds: WATER_QUESTION_IDS,
    extraAnalyticsQids: WATER_DERIVED_EXTRA_QIDS,
    applyDerivedTriggers: applyWaterDerivedTriggers,
  });

  useEffect(() => {

    const getLocations = async () => {
      try {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getSource`,
          {},
          {},
          "GET"
        );

        if (isSuccess && data?.data && Array.isArray(data.data)) {
          const labels = {};
          const ids = [];

          data.data.forEach((item) => {
            if (!item.id) return;

            const sourceLabel =
              item.unitCode || item.name || `Location ${item.id}`;

            if (
              item.subLocation &&
              Array.isArray(item.subLocation) &&
              item.subLocation.length > 0
            ) {
              labels[`${item.id}`] = sourceLabel;

              item.subLocation.forEach((location) => {
                if (!location.id) return;

                const key = `${item.id}-${location.id}`;

                const value = `${sourceLabel} - ${location.subLocation || "Sub Location"
                  }`;

                labels[key] = value;
                ids.push(key);
              });
            } else {
              const key = `${item.id}`;

              const { area, city, state, country, zipCode } =
                item?.location || {};

              const tmplocationParts = [
                area,
                city,
                state,
                country,
                zipCode,
              ].filter(Boolean);

              const tmplocation = tmplocationParts.length
                ? tmplocationParts.join(", ")
                : null;

              labels[key] =
                sourceLabel || tmplocation || `Location ${item.id}`;

              ids.push(key);
            }
          });

          setUserLabels(labels);
          setSourceIds(ids);
        }
      } catch (error) {
        console.error("Error fetching source labels:", error);
      }
    };

    getLocations();
    loadFinancialYears();
  }, []);

  /**
   * Populate the financial-year list and select the most recent year, along with
   * its months and an initial month. Reads the cached `financialYearData` first
   * and only calls the API when the cache is absent or unparseable, caching the
   * response for next time.
   */
  const loadFinancialYears = async () => {
    try {
      const storedData = localStorage.getItem("financialYearData");
      let financialYearData = null;

      if (storedData && isMounted.current) {
        try {
          financialYearData = JSON.parse(storedData);
        } catch (parseError) {
          console.error(
            "Error parsing stored financial year data:",
            parseError
          );
          localStorage.removeItem("financialYearData");
        }
      }

      if (financialYearData) {
        setFinancialYear(financialYearData);

        if (Array.isArray(financialYearData) && financialYearData.length > 0) {
          const latestYear = financialYearData[financialYearData.length - 1];
          if (latestYear && latestYear.id) {
            setFinancialYearId(latestYear.id);
            updateMonthsFromFinancialYear(latestYear);
            const generatedMonths = generateMonthsFromFinancialYear(latestYear);
            if (generatedMonths.length > 0) {
              setSelectedMonth(generatedMonths[0].value);
            }
          }
        }
      } else {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {},
          "GET"
        );

        if (isSuccess && data?.data && isMounted.current) {
          localStorage.setItem("financialYearData", JSON.stringify(data.data));
          setFinancialYear(data.data);

          if (Array.isArray(data.data) && data.data.length > 0) {
            const latestYear = data.data[data.data.length - 1];
            if (latestYear && latestYear.id) {
              setFinancialYearId(latestYear.id);
              updateMonthsFromFinancialYear(latestYear);
              const generatedMonths =
                generateMonthsFromFinancialYear(latestYear);
              if (generatedMonths.length > 0) {
                setSelectedMonth(generatedMonths[0].value);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error loading financial years:", error);
    }
  };

  /**
   * Refill the month dropdown for a financial year, defaulting the selection to
   * its first month only when no month is chosen yet.
   * @param {Object} financialYearData  the financial year to derive months from
   */
  const updateMonthsFromFinancialYear = (financialYearData) => {
    const generatedMonths = generateMonthsFromFinancialYear(financialYearData);
    setMonths(generatedMonths);
    if (generatedMonths.length > 0 && !selectedMonth) {
      setSelectedMonth(generatedMonths[0].value);
    }
  };

  /**
   * Switch financial year, rebuilding the month list and selecting that year's
   * first month. Clearing the year clears the months with it, since a month
   * without a year would query nothing.
   * @param {Object} event  change event from the financial-year select
   */
  const handleFinancialYearChange = (event) => {
    const newFinancialYearId = event.target.value;

    if (!newFinancialYearId) {
      setFinancialYearId(null);
      setMonths([]);
      setSelectedMonth(null);
      return;
    }

    const parsedId = parseInt(newFinancialYearId);
    setFinancialYearId(parsedId);

    const selectedYear = financialYear.find((year) => year.id === parsedId);
    if (selectedYear) {
      updateMonthsFromFinancialYear(selectedYear);
      const generatedMonths = generateMonthsFromFinancialYear(selectedYear);
      if (generatedMonths.length > 0) {
        setSelectedMonth(generatedMonths[0].value);
      }
    } else {
      setMonths([]);
      setSelectedMonth(null);
    }
  };

  /**
   * Replace the selected scope keys with the picker's current selection.
   * @param {string[]} selectedValues  scope keys chosen in the MultiSelect
   */
  const handleLocationChange = (selectedValues) => {
    setSelectedSourceIds(selectedValues);
  };

  /**
   * Switch the reported month; the blank option clears it.
   * @param {Object} event  change event from the month select
   */
  const handleMonthChange = (event) => {
    const newMonth = event.target.value;
    setSelectedMonth(newMonth || null);
  };

  /**
   * Show a tab. Only the active tab fetches, so this also decides which of the
   * two useAnalyticsData calls is live.
   * @param {string} tabKey  "bioMedical" or "water"
   */
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        padding: "20px",
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          .filter-card {
            background: rgba(255, 255, 255, 0.95);
            
            border-radius: 16px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            transition: all 0.3s ease;
          }
          
          .filter-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 40px rgba(0,0,0,0.15);
          }
          
          .tab-button {
            position: relative;
            overflow: hidden;
          }
          
          .tab-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.5s;
          }
          
          .tab-button:hover::before {
            left: 100%;
          }
        `}
      </style>

      <div
        className="filter-card"
        style={{
          marginBottom: "24px",
          padding: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: "200px", flex: "1" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "700",
                color: "#374151",
                fontSize: "14px",
              }}
            >
              <KpiIcon name="calendar" size={14} color="#374151" /> Financial Year
            </label>
            <select
              style={{
                padding: "12px 16px",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                width: "100%",
                fontSize: "14px",
                background: "#fff",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
              value={financialYearId || ""}
              onChange={handleFinancialYearChange}
              onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="">Select Financial Year</option>
              {Array.isArray(financialYear) &&
                financialYear.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.financial_year_value ||
                      item.name ||
                      `Year ${item.id}`}
                  </option>
                ))}
            </select>
          </div>

          {sourceIds && sourceIds.length === 1 ? (
            <></>
          ) : (
            <MultiSelect
              options={getLocationOptions()}
              selectedValues={selectedSourceIds}
              onChange={handleLocationChange}
              placeholder="Select Locations..."
              label="Location"
              icon="📍"
              activeTab={activeTab}
            />
          )}

          <div style={{ minWidth: "180px", flex: "1" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "700",
                color: "#374151",
                fontSize: "14px",
              }}
            >
              <KpiIcon name="calendar" size={14} color="#374151" /> Month
            </label>
            <select
              style={{
                padding: "12px 16px",
                borderRadius: "12px",
                border: "2px solid #e5e7eb",
                width: "100%",
                fontSize: "14px",
                background:
                  !financialYearId || months.length === 0 ? "#f9fafb" : "#fff",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                cursor:
                  !financialYearId || months.length === 0
                    ? "not-allowed"
                    : "pointer",
              }}
              value={selectedMonth || ""}
              onChange={handleMonthChange}
              disabled={!financialYearId || months.length === 0}
              onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            >
              <option value="">Select Month</option>
              {months.map((monthItem) => (
                <option key={monthItem.value} value={monthItem.value}>
                  {monthItem.fullName}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          {hasFramework48 && (
            <button
              style={{
                padding: "20px 32px",
                border: "none",
                background:
                  activeTab === "bioMedical"
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "transparent",
                color: activeTab === "bioMedical" ? "#fff" : "#6b7280",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "600",
                borderRadius: "0",
                transition: "all 0.3s ease",
                flex: "1",
              }}
              onClick={() => handleTabChange("bioMedical")}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <KpiIcon
                  name="trash"
                  size={18}
                  color={activeTab === "bioMedical" ? "#fff" : "#6b7280"}
                />
                Bio Medical Waste
              </div>
            </button>
          )}
          <button
            style={{
              padding: "20px 32px",
              border: "none",
              background:
                activeTab === "water"
                  ? "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
                  : "transparent",
              color: activeTab === "water" ? "#fff" : "#6b7280",
              cursor: "pointer",
              fontSize: "16px",
              fontWeight: "600",
              borderRadius: "0",
              transition: "all 0.3s ease",
              flex: "1",
            }}
            onClick={() => handleTabChange("water")}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              <KpiIcon
                name="drop"
                size={18}
                color={activeTab === "water" ? "#fff" : "#6b7280"}
              />
              Water Management
            </div>
          </button>
        </div>

        <div
          style={{
            padding: "32px",
            minHeight: "400px",
            background:
              activeTab === "bioMedical"
                ? "linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)"
                : "linear-gradient(135deg, #f0fdfa 0%, #ecfdf5 100%)",
          }}
        >
          {activeTab === "bioMedical" && hasFramework48 && (
            <AnalyticsTable
              title="Bio Medical Waste Management"
              subtitle="Monitor and track biomedical waste across all locations"
              headerIcon="trash"
              rowLabel="Waste Type"
              rowLabelIcon="note"
              accentGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              stripeColor="#fafbff"
              loadingLabel="Loading biomedical data..."
              emptyLabel="No biomedical questions available for the selected filters."
              exportPrefix="Biomedical_Waste_Report"
              categories={biomedical.categories}
              byLocation={biomedical.byLocation}
              loading={biomedical.loading}
              error={biomedical.error}
              refresh={biomedical.refresh}
              selectedMonth={selectedMonth}
              locations={locations}
              sourceLabels={sourceLabels}
              sourceIds={sourceIds}
              selectedSourceIds={selectedSourceIds}
              financialYear={financialYear}
              financialYearId={financialYearId}
            />
          )}

          {activeTab === "water" && (
            <AnalyticsTable
              title="Water Management"
              subtitle="Monitor water consumption and wastewater treatment across all locations"
              headerIcon="drop"
              rowLabel="Water Category"
              rowLabelIcon="drop"
              accentGradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
              stripeColor="#f0fdfa"
              loadingLabel="Loading water data..."
              emptyLabel="No water questions available for the selected filters."
              exportPrefix="Water_Management_Report"
              categories={water.categories}
              byLocation={water.byLocation}
              loading={water.loading}
              error={water.error}
              refresh={water.refresh}
              selectedMonth={selectedMonth}
              locations={locations}
              sourceLabels={sourceLabels}
              sourceIds={sourceIds}
              selectedSourceIds={selectedSourceIds}
              financialYear={financialYear}
              financialYearId={financialYearId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DateShow;